import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { MarkdownPreview } from '../preview/MarkdownPreview'
import { getSharedDocument } from '../../services/shareService'
import { ShareData } from '../../types/share'

/**
 * 分享页面组件
 * 显示通过分享链接访问的文档内容
 */
export const SharePage: React.FC = () => {
  const { t } = useTranslation()
  const { shareId } = useParams<{ shareId: string }>()
  const [shareData, setShareData] = useState<ShareData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!shareId) {
      setError('Invalid share link')
      setLoading(false)
      return
    }

    const loadSharedDocument = async () => {
      try {
        const data = await getSharedDocument(shareId)

        if (data) {
          // 检查是否过期
          if (data.expiresAt < Date.now()) {
            setError(t('share.expired'))
          } else {
            setShareData(data)
          }
        } else {
          setError(t('share.notFound'))
        }
      } catch (err) {
        console.error('Error loading shared document:', err)
        setError(t('share.error'))
      } finally {
        setLoading(false)
      }
    }

    loadSharedDocument()
  }, [shareId, t])

  if (loading) {
    return (
      <div className="share-loading">
        <div className="loading-spinner"></div>
        <p>{t('share.loading')}</p>
      </div>
    )
  }

  if (error || !shareData) {
    return (
      <div className="share-error">
        <h2>{t('share.errorTitle')}</h2>
        <p>{error || t('share.notFound')}</p>
        <p className="share-help">
          {t('share.help')}
        </p>
      </div>
    )
  }

  // 计算剩余时间
  const now = Date.now()
  const timeLeft = shareData.expiresAt - now
  const hoursLeft = Math.max(0, Math.floor(timeLeft / (1000 * 60 * 60)))
  const minutesLeft = Math.max(0, Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60)))

  return (
    <div className="share-page">
      {/* 分享头部信息 */}
      <div className="share-header">
        <div className="share-title">
          <h1>{shareData.title}</h1>
          <div className="share-meta">
            <span className="shared-from">
              {t('share.sharedFrom')}
            </span>
            <span className="time-left">
              {t('share.expiresIn', { hours: hoursLeft, minutes: minutesLeft })}
            </span>
          </div>
        </div>
      </div>

      {/* 分享内容 */}
      <div className="share-content">
        <MarkdownPreview content={shareData.content} className="py-4" />
      </div>

      {/* 分享底部 */}
      <div className="share-footer">
        <p className="share-notice">
          {t('share.notice')}
        </p>
      </div>

      <style>{`
        .share-page {
          min-height: 100vh;
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .share-header {
          border-bottom: 1px solid var(--border-light);
          padding: 40px 20px;
          background: var(--bg-primary);
        }

        .share-title h1 {
          margin: 0 0 16px 0;
          font-size: 2em;
          font-weight: 600;
          color: var(--text-primary);
          line-height: 1.2;
        }

        .share-meta {
          display: flex;
          flex-direction: column;
          gap: 8px;
          font-size: 14px;
          color: var(--text-secondary);
        }

        .shared-from {
          font-weight: 500;
        }

        .time-left {
          font-size: 12px;
          color: #ff6b6b;
        }

        .share-content {
          padding: 40px 20px;
          max-width: 800px;
          margin: 0 auto;
        }

        .share-footer {
          border-top: 1px solid var(--border-light);
          padding: 20px;
          text-align: center;
          background: var(--bg-primary);
        }

        .share-notice {
          font-size: 12px;
          color: var(--text-tertiary);
          margin: 0;
        }

        .share-error {
          padding: 60px 20px;
          text-align: center;
          color: var(--text-secondary);
        }

        .share-error h2 {
          color: #ff6b6b;
          margin-bottom: 16px;
        }

        .share-help {
          font-size: 14px;
          margin-top: 16px;
          color: var(--text-tertiary);
        }

        .share-loading {
          padding: 60px 20px;
          text-align: center;
          color: var(--text-secondary);
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border-light);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .share-header {
            padding: 20px;
          }

          .share-content {
            padding: 20px;
          }

          .share-title h1 {
            font-size: 1.5em;
          }
        }
      `}</style>
    </div>
  )
}